import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrefs, useFlags } from 'wsm-common-data';
import { isBrowser } from 'ws-scripts/modules/environment';
import { ErrorBoundary } from 'wsm-error-boundary';
import logger from 'ws-scripts/modules/logger';

import PaymentSelectorView from '../components/PaymentSelectorView';
import StepContainer from '../components/StepContainer';
import { LOGGING_ATTR_PREFIX } from '../constants';
import { setSteps } from '../features/steps/stepsSlice';
import { setVehicleId } from '../features/vehicleId/vehicleIdSlice';
import { setListingType } from '../features/listingType/listingTypeSlice';

import {
	getLsWidgetStateByVehicleId,
	migrateLsStepsWithEnabledFlags
} from '../helpers';
import { errorBoundaryHandler } from '../helpers/logging-client';
import useAMDAutoOpen from '../hooks/useAMDAutoOpen';
import useUdsLeadGate from '../hooks/useUdsLeadGate';
import usePaymentData from '../hooks/usePaymentData';
import { TEST_FUTURE_FLAG } from '../hooks/usePaymentData/constants';

import '../global-sass/main.scss';

const Widget = () => {
	const { trackMyOfferView, showCTAs } = usePrefs();
	const flags = useFlags();
	const dispatch = useDispatch();
	const steps = useSelector((state) => state.steps);
	const isPaymentSelectorView = trackMyOfferView === 'paymentSelector';
	const isLeadGateActive = useUdsLeadGate();
	// Sentinel ref to locate this widget's outer container in the DOM
	const sentinelRef = useRef(null);

	const isTestFuture = flags[TEST_FUTURE_FLAG] || false;
	// Ensure payment event listeners are always active so paymentsInfo store gets populated
	// Skip when PaymentSelector is rendered (it calls usePaymentData itself)
	// Only enabled when test-future flag is ON
	usePaymentData({ enabled: isTestFuture && !isPaymentSelectorView });

	// Hide the outer DDC container when lead gate is active
	useEffect(() => {
		const outerContainer = sentinelRef.current?.closest('[data-widget-id]');
		if (outerContainer) {
			outerContainer.style.display = isLeadGateActive ? 'none' : '';
		}
	}, [isLeadGateActive, isPaymentSelectorView]);

	// Publish isLeadGateActive and showCTAs to ws-vehicle-toolbar
	useEffect(() => {
		const pubsub = isBrowser && window?.DDC?.pubsub;
		if (pubsub) {
			pubsub.publish('ws-vehicle-toolbar/lead-gate-active', {
				isLeadGateActive,
				showCTAs
			});
		}
	}, [isLeadGateActive, showCTAs]);

	// Handle AMD auto-open from SRP
	useAMDAutoOpen();
	
	useEffect(() => {
		try {
			// TODO: refactor to set these on server side
			// Safe access with optional chaining
			const uuid = window?.DDC?.dataLayer?.vehicles?.[0]?.uuid || '';
			const listingType =
				window?.DDC?.dataLayer?.vehicles?.[0]?.inventoryType || '';

			// payment selector view handles read, write from local storage and publish steps to step tracker
			if (uuid && isPaymentSelectorView) {
				const lsSteps = getLsWidgetStateByVehicleId(uuid, steps);

				// Migrate the lsStep (add enabled flag from server state)
				const migratedSteps = migrateLsStepsWithEnabledFlags(
					lsSteps,
					steps
				);

				// TODO: validate LS steps before pushing to state
				dispatch(setSteps(migratedSteps));

				// TODO: refactor to set these on server side
				dispatch(setVehicleId(uuid));
				dispatch(setListingType(listingType));
			}
		} catch (error) {
			logger.error(
				`${LOGGING_ATTR_PREFIX}: Widget initialization failed`,
				error
			);
			// Widget still renders with server-set state
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (isPaymentSelectorView) {
		return <PaymentSelectorView />;
	}

	return (
		<>
			<span ref={sentinelRef} style={{ display: 'none' }} aria-hidden="true" />
			<StepContainer hidden={isLeadGateActive} />
		</>
	);
};

// Wrap Widget with ErrorBoundary to prevent crashes from breaking the page
const WidgetWithErrorBoundary = () => (
	<ErrorBoundary
		errorMarkup={<div>There was an error retrieving payment info.</div>}
		errorHandler={errorBoundaryHandler}
		newRelicPageAction={`${LOGGING_ATTR_PREFIX}-error-boundary`}
	>
		<Widget />
	</ErrorBoundary>
);

export default WidgetWithErrorBoundary;
